import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Parameter } from '../base/parameter';
import { Metamodel } from 'app/model/base/model';
import { AdapterContext, generateIdentity, NamedAdapter, setIdentity } from 'shell-models-lib';
import { convertParameterValue, stringifyParameterValue } from 'app/shared/utils/parameter-converter.util';
import {
    IPipelineParameterVariableContext,
    ParameterVariableTypeContext
} from 'app/interfaces/copilot.pipeline.action.types';
import { getString } from 'app/shared/utils/string.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(Parameter)
})
export class ParameterAdapter extends NamedAdapter<Parameter, Model.ParameterSpecification> {
    public static convertParameterTypeToFabricContext(value: string): ParameterVariableTypeContext {
        switch (value?.toLowerCase()) {
            case 'string':
                return ParameterVariableTypeContext.String;
            case 'int':
                return ParameterVariableTypeContext.Int;
            case 'float':
                return ParameterVariableTypeContext.Float;
            case 'bool':
                return ParameterVariableTypeContext.Bool;
            case 'array':
                return ParameterVariableTypeContext.Array;
            case 'object':
                return ParameterVariableTypeContext.Object;
            case 'securestring':
                return ParameterVariableTypeContext.SecureString;
            case 'integer':
                return ParameterVariableTypeContext.Integer;
            case 'boolean':
                return ParameterVariableTypeContext.Boolean;
            default:
                return undefined;
        }
    }

    public static getParameterTypeFromFabricContext(value: ParameterVariableTypeContext): string {
        switch (value) {
            case ParameterVariableTypeContext.String:
                return 'string';
            case ParameterVariableTypeContext.Int:
                return 'int';
            case ParameterVariableTypeContext.Float:
                return 'float';
            case ParameterVariableTypeContext.Bool:
                return 'bool';
            case ParameterVariableTypeContext.Array:
                return 'array';
            case ParameterVariableTypeContext.Object:
                return 'object';
            case ParameterVariableTypeContext.SecureString:
                return 'securestring';
            case ParameterVariableTypeContext.Integer:
                return 'Integer';
            case ParameterVariableTypeContext.Boolean:
                return 'Boolean';
            default:
                return undefined;
        }
    }

    public override toFabricCopilotContext(parameter: Parameter): IPipelineParameterVariableContext {
        if (parameter) {
            const parameterResource = this.toResource(parameter);
            const result: IPipelineParameterVariableContext = {
                Name: getString(parameter.name),
                Type: ParameterAdapter.convertParameterTypeToFabricContext(parameterResource.type),
                DefaultValue: parameterResource.defaultValue
            };
            return result;
        }
        return undefined;
    }

    public override fromFabricCopilotContext(context: IPipelineParameterVariableContext, model?: Parameter): Parameter {
        if (context) {
            model = this._toModel(
                {
                    type: ParameterAdapter.getParameterTypeFromFabricContext(context.Type),
                    defaultValue: context.DefaultValue ?? undefined
                },
                model,
                context.Name
            );
        }
        return model;
    }

    protected _toResource(parameter: Parameter): Model.ParameterSpecification {
        const parameterSpecification: Model.ParameterSpecification = {
            type: parameter._type
        };
        if (parameter.defaultValue !== undefined) {
            parameterSpecification.defaultValue = convertParameterValue(
                parameter.defaultValue,
                parameter._type,
                'ParameterAdapter',
                this.getNullableLogger()
            );
        }

        if (this.context.writeIdentities) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (parameterSpecification as any).identity = this.context.identityProvider.identity(parameter);
        }
        return parameterSpecification;
    }

    protected _toModel(
        modelParameter: Model.ParameterSpecification,
        parameter?: Parameter,
        parameterName?: string,
        ownerType?: string,
        ownerName?: string
    ): Parameter {
        if (!parameter) {
            parameter = new Parameter(parameterName);
        }
        // eslint-disable-next-line
        if (modelParameter.defaultValue !== undefined) {
            parameter.defaultValue = stringifyParameterValue(modelParameter.defaultValue);
        }
        parameter._type = modelParameter.type;

        if (this.context.adapterConfig.replaceIdentitiesForNonConcepts) {
            // If we are getting these assertion we have to fix the calling code.
            const parameterIdentity = AdapterContext.getIdentity(
                generateIdentity(ownerType, ownerName),
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                parameter.name!
            );

            setIdentity(parameter, parameterIdentity);
        } else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const resource = modelParameter as any;
            if (resource.identity) {
                setIdentity(parameter, resource.identity);
            }
        }

        return parameter;
    }
}
